import { Fragment, h } from 'preact';
import {
	useState, useEffect, useContext,
} from 'preact/compat';
import { decodeEntities } from '@wordpress/html-entities';
import { useDonationStep, DonationStepEnum } from '../hooks/step';
import {
	useCart, CartActionType, CartSelection, formatCurrency,
} from '../hooks/cart';
import { Theme } from '../lib/render';
import { validCheckoutTransition, validContributionAmountWarning, validSelectionAmounts } from '../hooks/transitionGaurds';
import CustomAmount from './inputs/CustomAmount';
import Chevron from '../icons/Chevron';

/**
 * Single Selection.
 */
const SingleSelection = ({
	id,
	selection,
	isEditing,
}: {
	id: number,
	selection: CartSelection;
	isEditing: boolean;
}) => {
	const { dispatch } = useCart();
	const warning = validContributionAmountWarning(selection);
	return (
		<div className="ea-inventory-selection">
			<div className="ea-inventory-selection__name">
				<label
					className={[
						'h6',
						warning ? 'error' : '',
					].join(' ')}
					htmlFor={`candidate-${id}`}
				>
					{decodeEntities(selection.name)}
				</label>
				{warning && (
					<span className="error ea-inventory-selection__warning ea-small">
						{warning}
					</span>
				)}
				{isEditing && (
					<button
						type="button"
						className="ea-btn-reset ea-underline ea-xsmall"
						onClick={() => {
							dispatch({
								type: CartActionType.REMOVE_SELECTION,
								payload: { id },
							});
						}}
					>
						Remove
						<span className="screen-reader-text">
							Delete
							{formatCurrency(selection.amount)}
							{' '}
							for
							{decodeEntities(selection.name)}
						</span>
					</button>
				)}
			</div>
			<div className="ea-inventory-selection__manual">
				{isEditing ? (
					<CustomAmount
						className="ea-inventory-selection__amount"
						target={selection}
						matchValue
						forceActive={isEditing}
						id={`candidate-${id}`}
					/>
				) : (
					<span className="ea-inventory-selection__manual__default">
						{formatCurrency(selection.amount)}
					</span>
				)}
			</div>
		</div>
	);
};

/**
 * Inventory Inner.
 * Displays the selection editing UI + list.
 *
 * @param {boolean} props.fallback Whether to display the instructions when there is no selection.
 */
const InventoryInner = ({
	isEditing,
	setIsEditing,
	selections,
	total,
	step,
	inventory,
	allowUserInput,
	fallback = true,
}:{
	isEditing: boolean;
	setIsEditing: (isEditing: boolean) => void;
	selections: Map<number, CartSelection>;
	total: number;
	step: DonationStepEnum;
	inventory: any;
	allowUserInput: boolean;
	fallback?: boolean;
}) => {
	if (selections.size > 0) {
		return (
			<Fragment>
				<div className="ea-inventory__edit">
					<small className="ea-small">Candidates</small>
					{/**
					 * Edit is available only before the confirm step.
					 */
						step !== DonationStepEnum.CONFIRM && allowUserInput && (
							<button
								type="button"
								className="ea-btn-reset ea-small ea-underline"
								onClick={() => {
									setIsEditing(!isEditing);
								}}
								disabled={isEditing && !validSelectionAmounts()}
							>
								{isEditing ? 'Save' : 'Edit'}
							</button>
						)
					}
				</div>
				<ul className="ea-inventory__selection-list">
					{Array.from(selections).map(([id, selection]) => (
						<li key={id}>
							<SingleSelection id={id} selection={selection} isEditing={isEditing} />
						</li>
					))}
				</ul>
				<div className="ea-inventory__total">
					<small className="ea-small">Total Contribution</small>
					<small className="h5">
						{formatCurrency(total)}
					</small>
				</div>
			</Fragment>
		);
	}

	if (fallback) {
		return (
			<p className="ea-small">
				{inventory.instructions}
			</p>
		);
	}

	return null;
};

/**
 * Inventory component.
 * Allows changing donation allocation.
 *
 * Notes:
 * - if cart selections are cleared, user is returned to entry step.
 * - if user is in edit mode and step transitions, user will leave edit mode.
 * - user input immeidately affects cart selections.
 */
const Inventory = () => {
	const step = useDonationStep();
	const { cart } = useCart();
	const { inventory } = useContext(Theme);
	const [isEditing, setIsEditing] = useState(false);
	const { selections, total, allowUserInput } = cart;

	/**
	 * Stop editing when step or allowUserInput changes.
	 */
	useEffect(() => {
		if (isEditing === true) {
			setIsEditing(false);
		}
	}, [step.state, allowUserInput]);

	return (
		<div className="ea-inventory">
			<div className="ea-inventory__inner">
				<h5 className="ea-inventory__title">{inventory.title}</h5>
				<InventoryInner
					isEditing={isEditing}
					setIsEditing={setIsEditing}
					selections={selections}
					total={total}
					step={step.state}
					inventory={inventory}
					allowUserInput={cart.allowUserInput}
				/>
				{/* Entry -> Checkout */}
				{step.state === DonationStepEnum.ENTRY && (
					<button
						className={[
							'ea-btn',
							'ea-inventory__continue',
							selections.size > 0 ? '' : 'ea-btn--invert',
						].join(' ')}
						type="button"
						// Must have selections.
						disabled={!validCheckoutTransition()}
						onClick={() => {
							step.dispatch({
								type: 'MANUAL_STEP',
								payload: {
									step: DonationStepEnum.CHECKOUT,
								},
							});
						}}
					>
						<span>{(selections.size > 0) ? 'Donate' : 'Select Candidates'}</span>
					</button>
				)}
				<small className="ea-xsmall">Minimum donation of $3 per selection</small>
			</div>
		</div>
	);
};

/**
 * Mobile inventory component.
 * Usually set as fixed to bottom of screen.
 */
export const InventoryDrawer = () => {
	const step = useDonationStep();
	const { cart } = useCart();
	const { inventory } = useContext(Theme);
	const [isEditing, setIsEditing] = useState(false);
	const { selections, total, allowUserInput } = cart;
	const [expanded, setExpanded] = useState(false);
	const hasSelections = selections.size > 0;

	/**
	 * Close when no selections.
	 */
	useEffect(() => {
		if (expanded && !hasSelections) {
			setExpanded(false);
		}
	}, [hasSelections, expanded]);

	/**
	 * Stop editing when step or allowUserInput changes.
	 */
	useEffect(() => {
		if (isEditing === true) {
			setIsEditing(false);
		}
	}, [step.state, allowUserInput]);

	/**
	 * Only at entry.
	 */
	if (step.state !== DonationStepEnum.ENTRY) {
		return null;
	}

	return (
		<div className="ea-inventory ea-inventory--mobile">
			<div className="ea-inventory__inner">
				<div className="ea-inventory-drawer">
					<button
						type="button"
						aria-expanded={expanded}
						className={[
							'ea-inventory-drawer__trigger',
							'ea-btn-reset',
							expanded ? 'ea-inventory-drawer__trigger--active' : 'ea-inventory-drawer__trigger--inactive',
						].join(' ')}
						aria-controls="ea-inventory-drawer"
						id="trigger-ea-inventory-drawer"
						onClick={() => setExpanded(!expanded)}
						disabled={!hasSelections}
					>
						<div
							aria-hidden={(expanded || !hasSelections)}
							className={[
								'ea-inventory-drawer__top',
								(expanded || !hasSelections) ? 'ea-inventory-drawer__top--close' : '',
							].join(' ')}
						>
							<span className="ea-small">Click to view details</span>
						</div>
						<h5 className="ea-inventory-drawer__title">
							<span>{inventory.title}</span>
							{expanded ? (
								<span><Chevron /></span>
							) : (
								<span>{formatCurrency(total)}</span>
							)}
						</h5>
					</button>
					<div
						id="ea-inventory-drawer"
						role="region"
						aria-labelledby="trigger-ea-inventory-drawer"
						className="ea-inventory-drawer__panel"
						hidden={!expanded}
						aria-hidden={!expanded}
					>
						<div className="ea-inventory-drawer__panel-hider">
							<div>
								<InventoryInner
									isEditing={isEditing}
									setIsEditing={setIsEditing}
									selections={selections}
									total={total}
									step={step.state}
									inventory={inventory}
									fallback={false}
									allowUserInput={cart.allowUserInput}
								/>
							</div>
						</div>
					</div>
				</div>
				{/* Inventory Sub Nav */}
				{hasSelections && (
					<button
						className={[
							'ea-btn',
							'ea-inventory__continue',
							selections.size > 0 ? '' : 'ea-btn--invert',
						].join(' ')}
						type="button"
						// Must have selections.
						disabled={!validCheckoutTransition()}
						onClick={() => {
							step.dispatch({
								type: 'MANUAL_STEP',
								payload: {
									step: DonationStepEnum.CHECKOUT,
								},
							});
						}}
					>
						<span>Donate</span>
					</button>
				)}
			</div>
		</div>
	);
};

/**
 * Individual inventory component.
 * Displays total and edit button.
 */
export const IndividualInventory = () => {
	const { dispatch } = useDonationStep();
	const { cart } = useCart();
	const { total } = cart;

	return (
		<div className="ea-individual__inventory">
			<span className="h5">
				Donating
				{` ${formatCurrency(total)}`}
			</span>
			<button
				type="button"
				className="ea-btn-reset ea-small ea-underline"
				onClick={() => {
					dispatch({
						type: 'MANUAL_STEP',
						payload: {
							step: DonationStepEnum.ENTRY,
						},
					});
				}}
			>
				<span>Edit</span>
			</button>
		</div>
	);
};

export default Inventory;
